import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

export default function ReportIncidentModal({ patrolId = null, onClose, onSuccess }) {
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity]       = useState('medium');
  const [photo, setPhoto]             = useState(null);
  const [preview, setPreview]         = useState(null);
  const [coords, setCoords]           = useState(null);
  const [gettingGps, setGettingGps]   = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const fileInputRef                  = useRef(null);

  // Auto-acquire GPS on modal mount
  useEffect(() => {
    if (!navigator.geolocation) {
      setGettingGps(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy || 10,
        });
        setGettingGps(false);
      },
      (err) => {
        console.warn('Geolocation error:', err);
        setGettingGps(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 15000 }
    );
  }, []);

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const MAX_DIM = 1280;
        let width = img.width;
        let height = img.height;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.80);
      };
      img.src = url;
    });
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setPhoto(compressed);
      const url = URL.createObjectURL(compressed);
      setPreview(url);
    } catch {
      setPhoto(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleClearPhoto = () => {
    setPhoto(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Please enter an incident title');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('description', description.trim());
      fd.append('severity', severity);
      if (patrolId) fd.append('patrol_id', patrolId);
      if (coords) {
        fd.append('latitude', coords.lat.toFixed(7));
        fd.append('longitude', coords.lng.toFixed(7));
      }
      if (photo) {
        fd.append('image', photo);
      }

      await axios.post('/api/incidents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Incident reported successfully! ⚠️');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit incident report';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const severities = [
    { id: 'low',      label: 'Low',      color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
    { id: 'medium',   label: 'Medium',   color: '#d97706', bg: '#fef3c7', border: '#fde68a' },
    { id: 'high',     label: 'High',     color: '#ea580c', bg: '#ffedd5', border: '#fed7aa' },
    { id: 'critical', label: 'Critical', color: '#dc2626', bg: '#fef2f2', border: '#fecdd3' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)',
          zIndex: 1200,
        }}
      />

      {/* Modal Container */}
      <div
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '92%', maxWidth: 500,
          maxHeight: '92vh',
          background: 'white',
          borderRadius: 20,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          zIndex: 1201,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #1e293b, #0f172a)',
          color: 'white',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              ⚠️ Report Incident
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              {patrolId ? 'Linked to current active patrol' : 'General security / site incident'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: '#94a3b8',
              fontSize: 22, cursor: 'pointer', padding: '0 4px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} style={{ padding: '20px', overflowY: 'auto' }}>
          {/* Title input */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
              Incident Title *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Broken Lock, Water Leakage, Unauthorized Intruder"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 10,
                border: '1.5px solid #cbd5e1', fontSize: 14, outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Severity selector */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
              Severity Level
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {severities.map(s => {
                const active = severity === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSeverity(s.id)}
                    style={{
                      padding: '9px 4px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                      cursor: 'pointer', textAlign: 'center', textTransform: 'capitalize',
                      border: `2px solid ${active ? s.color : s.border}`,
                      background: active ? s.color : s.bg,
                      color: active ? 'white' : s.color,
                      boxShadow: active ? `0 3px 8px ${s.color}35` : 'none',
                      transition: 'all .15s',
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
              Description & Notes
            </label>
            <textarea
              rows={3}
              placeholder="Describe what occurred, exact location inside the site, actions taken..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 10,
                border: '1.5px solid #cbd5e1', fontSize: 13, outline: 'none',
                fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Photo Evidence */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
              📸 Photo Evidence (Optional)
            </label>

            {preview ? (
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: '1.5px solid #cbd5e1' }}>
                <img
                  src={preview}
                  alt="Preview"
                  style={{ width: '100%', maxHeight: 180, objectFit: 'cover', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={handleClearPhoto}
                  style={{
                    position: 'absolute', top: 8, right: 8,
                    background: 'rgba(239, 35, 60, 0.9)', color: 'white',
                    border: 'none', borderRadius: 20, padding: '4px 10px',
                    fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  ✕ Remove
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed #cbd5e1', borderRadius: 12,
                  padding: '18px', textAlign: 'center', cursor: 'pointer',
                  background: '#f8fafc', transition: 'border-color .2s',
                }}
              >
                <div style={{ fontSize: 26, marginBottom: 4 }}>📷</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                  Tap to Take Photo or Upload
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  Supports camera capture on mobile devices
                </div>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* GPS Location Status (strictly optional) */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: coords ? '#f0fdf4' : '#f8fafc',
            border: `1px solid ${coords ? '#bbf7d0' : '#e2e8f0'}`,
            borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>📍</span>
              <span style={{ color: coords ? '#166534' : '#64748b', fontWeight: 600 }}>
                {gettingGps
                  ? 'Detecting GPS… (optional)'
                  : coords
                  ? `GPS: ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)} (±${Math.round(coords.acc)}m)`
                  : 'GPS not available (not required)'}
              </span>
            </div>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
              background: coords ? '#dcfce7' : '#f1f5f9',
              color: coords ? '#166534' : '#64748b',
            }}>
              {coords ? '✓ Attached' : 'Optional'}
            </span>
          </div>

          {/* Submit & Cancel Buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #cbd5e1',
                background: 'white', color: '#64748b', fontWeight: 600, fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 2, padding: '12px', borderRadius: 10, border: 'none',
                background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #ef233c, #dc2626)',
                color: 'white', fontWeight: 800, fontSize: 14,
                cursor: submitting ? 'not-allowed' : 'pointer',
                boxShadow: submitting ? 'none' : '0 4px 14px rgba(239,35,60,0.35)',
              }}
            >
              {submitting ? '⏳ Submitting…' : '⚠️ Submit Incident'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
